import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  COLOR_PRESETS,
  TIME_SLOT_TARGETS,
  type TimeSlotTarget,
} from './timeSlotForm';

export type TimeSlotFormData = {
  title: string;
  // Blank on create = derived from the title and weekday; kept as is on edit.
  slug: string;
  description: string;
  // Both instants are held as `<input type="datetime-local">` values while the dialog is
  // open and converted on submit; see timeSlotForm.ts.
  startTime: string;
  endTime: string;
  location: string;
  color: string;
  target: TimeSlotTarget[];
};

interface TimeSlotFormFieldsProps {
  idPrefix: string;
  data: TimeSlotFormData;
  onChange: (patch: Partial<TimeSlotFormData>) => void;
  errors: Record<string, string | undefined>;
  processing: boolean;
}

// The create and edit dialogs of the Next app carried the same eight fields twice over;
// here they are one component so the two dialogs cannot drift apart.
export function TimeSlotFormFields({
  idPrefix,
  data,
  onChange,
  errors,
  processing,
}: TimeSlotFormFieldsProps) {
  const toggleTarget = (target: TimeSlotTarget, checked: boolean) =>
    onChange({
      target: checked
        ? [...data.target, target]
        : data.target.filter((value) => value !== target),
    });

  return (
    <>
      <div className="grid gap-2">
        <Label htmlFor={`${idPrefix}-title`}>Title</Label>
        <Input
          id={`${idPrefix}-title`}
          placeholder="Opening Ceremony"
          disabled={processing}
          value={data.title}
          onChange={(e) => onChange({ title: e.target.value })}
        />
        {errors.title && (
          <p className="text-destructive text-sm">{errors.title}</p>
        )}
      </div>

      <div className="grid gap-2">
        <Label htmlFor={`${idPrefix}-slug`}>Link slug</Label>
        <Input
          id={`${idPrefix}-slug`}
          placeholder="almuerzo-sabado (auto)"
          disabled={processing}
          value={data.slug}
          onChange={(e) => onChange({ slug: e.target.value })}
        />
        <p className="text-muted-foreground text-xs">
          Shared as ?slot={data.slug || '…'} on the schedule page.
        </p>
        {errors.slug && (
          <p className="text-destructive text-sm">{errors.slug}</p>
        )}
      </div>

      <div className="grid gap-2">
        <Label htmlFor={`${idPrefix}-description`}>Description</Label>
        <Textarea
          id={`${idPrefix}-description`}
          placeholder="Brief description of the event..."
          disabled={processing}
          value={data.description}
          onChange={(e) => onChange({ description: e.target.value })}
        />
        {errors.description && (
          <p className="text-destructive text-sm">{errors.description}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor={`${idPrefix}-start-time`}>Start Time</Label>
          <Input
            id={`${idPrefix}-start-time`}
            type="datetime-local"
            disabled={processing}
            value={data.startTime}
            onChange={(e) => onChange({ startTime: e.target.value })}
          />
          {errors.startTime && (
            <p className="text-destructive text-sm">{errors.startTime}</p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor={`${idPrefix}-end-time`}>End Time</Label>
          <Input
            id={`${idPrefix}-end-time`}
            type="datetime-local"
            disabled={processing}
            value={data.endTime}
            onChange={(e) => onChange({ endTime: e.target.value })}
          />
          {errors.endTime && (
            <p className="text-destructive text-sm">{errors.endTime}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor={`${idPrefix}-location`}>Location</Label>
          <Input
            id={`${idPrefix}-location`}
            placeholder="Main Hall"
            disabled={processing}
            value={data.location}
            onChange={(e) => onChange({ location: e.target.value })}
          />
          {errors.location && (
            <p className="text-destructive text-sm">{errors.location}</p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor={`${idPrefix}-color`}>Color</Label>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Input
                id={`${idPrefix}-color`}
                type="color"
                disabled={processing}
                value={data.color}
                onChange={(e) => onChange({ color: e.target.value })}
                className="h-10 w-20"
              />
              <Input
                type="text"
                disabled={processing}
                value={data.color}
                onChange={(e) => onChange({ color: e.target.value })}
                placeholder="#f1c40f"
                className="flex-1"
              />
            </div>
            <div className="flex gap-2">
              {COLOR_PRESETS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => onChange({ color: color.value })}
                  disabled={processing}
                  className="h-8 w-8 rounded border-2 transition-all hover:scale-110"
                  style={{
                    backgroundColor: color.value,
                    borderColor:
                      data.color === color.value ? '#333' : 'transparent',
                  }}
                  title={color.name}
                />
              ))}
            </div>
          </div>
          {errors.color && (
            <p className="text-destructive text-sm">{errors.color}</p>
          )}
        </div>
      </div>

      <div>
        <div className="mb-4">
          <Label className="text-base">Target Audience</Label>
          <p className="text-muted-foreground text-sm">
            Select who this time slot is relevant for
          </p>
        </div>
        {TIME_SLOT_TARGETS.map((target) => (
          <div
            key={target}
            className="flex flex-row items-start gap-3 space-y-0"
          >
            <Checkbox
              id={`${idPrefix}-target-${target}`}
              checked={data.target.includes(target)}
              onCheckedChange={(checked) => toggleTarget(target, checked === true)}
            />
            <Label
              htmlFor={`${idPrefix}-target-${target}`}
              className="font-normal capitalize"
            >
              {target}
            </Label>
          </div>
        ))}
        {errors.target && (
          <p className="text-destructive text-sm">{errors.target}</p>
        )}
      </div>
    </>
  );
}
